import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Button } from "@components/ui/Button"
import { Chip } from "@components/ui/Chip"
import { formatDateTime } from "@utils/date-format"
import { cn, cx, tv } from "@utils/tw"
import { motion, MotionProps } from "motion/react"
import { BrowseFilmTileFragment } from "@generated-types"
import { FadeScroller } from "@components/atoms/FadeScroller"
import { SelectableSessionItem } from "./SelectableSessionItem"
import { Suspense, useState } from "react"
import dayjs from "dayjs"
import { Link } from "eddev/routing"
import { YouTubeVideo } from "@components/media/YouTubeVideo"
import { useAutoplayTrailer } from "@hooks/useAutoplayTrailer"
import { trackFilmInterest } from "@features/tracking/film-history"

type Props = {
  film: BrowseFilmTileFragment
  onClose?: () => void
}

export function FilmPreviewTile(props: Props) {
  const { autoplayTrailer, setAutoplayTrailer } = useAutoplayTrailer()
  const [isPlaying, setIsPlaying] = useState(false)
  const [canPlayTrailer, setCanPlayTrailer] = useState(autoplayTrailer)
  const styles = createStyles()

  const meta: string[] = []
  if (props.film.credits?.director?.length) {
    meta.push(`Directed by ${props.film.credits.director.join(", ")}`)
  }
  if (props.film.filmInfo?.year) {
    meta.push(props.film.filmInfo.year)
  }
  if (props.film.filmInfo?.primaryCountry) {
    meta.push(props.film.filmInfo.primaryCountry)
  }
  if (props.film.eventInfo?.runtime) {
    meta.push(`${props.film.eventInfo.runtime} minutes`)
  }
  if (props.film.eventInfo?.classification) {
    meta.push(props.film.eventInfo.classification)
  }

  const nextSessions = props.film.sessions?.filter((session) =>
    dayjs(session?.info?.dateTime!).add(1.5, "h").isAfter(dayjs()),
  )

  return (
    <motion.div initial="hide" animate="show" exit="hide" className={styles.base()}>
      <motion.div
        className={styles.info()}
        initial="hide"
        animate="show"
        exit="hide"
        transition={{ staggerChildren: 0.03 }}
      >
        <div className="flex flex-col gap-4">
          <motion.div className={styles.title()} {...anim()}>
            {props.film.title}
          </motion.div>
          <motion.div className={styles.meta()}>
            {meta.map((text, i) => (
              <motion.div {...anim()} key={i}>
                <Chip shape="rounded" variant="mid">
                  {text}
                </Chip>
              </motion.div>
            ))}
          </motion.div>
          <motion.p
            className={styles.description()}
            dangerouslySetInnerHTML={{ __html: props.film.descriptions?.short! }}
            {...anim()}
          ></motion.p>
        </div>
        <div className={styles.sessions()}>
          {nextSessions?.length! > 0 && (
            <motion.div className="type-label-caps text-fg-secondary mb-3" {...anim()}>
              Next Sessions
            </motion.div>
          )}
          <div className="flex gap-3 items-center">
            <FadeScroller className="flex flex-1 gap-2 scroll-mask-6">
              {nextSessions?.map((session, i) => (
                <motion.div key={i} {...anim()}>
                  <Link href={props.film?.uri! + "#tickets-" + session?.info?.ferveId} className="w-full">
                    <SelectableSessionItem session={session!} film={props.film} />
                  </Link>
                </motion.div>
              ))}
            </FadeScroller>
            <motion.div {...anim()}>
              <Button href={props.film.uri + "#tickets"} shape="branded" variant="hero" aria-label="Book Tickets">
                {nextSessions?.length! > 0 ? "Book Tickets" : "More Info"}
              </Button>
            </motion.div>
          </div>
        </div>
      </motion.div>
      <motion.div className={styles.media()} {...anim()}>
        <div className="absolute top-0 right-0 z-[1]">
          {props.onClose && (
            <Button
              shape="icon"
              variant="ghost"
              onClick={props.onClose}
              icon="cross"
              className="absolute top-2 right-2 !bg-transparent hover:!bg-black/40 active:!bg-black/80"
            />
          )}
        </div>
        <>
          <ResponsiveImage {...props.film.media?.thumb} max objectFit="cover" />
          {canPlayTrailer && !!props.film.eventInfo?.trailer && (
            <Suspense>
              <YouTubeVideo
                videoId={props.film.eventInfo?.trailer!}
                onPlaying={() => {
                  setIsPlaying(true)
                }}
                onError={() => {
                  setIsPlaying(true)
                }}
                startSeconds={3}
                onPaused={() => setAutoplayTrailer(false)}
                className={cn(
                  "absolute w-full h-full opacity-0 pointer-events-none",
                  isPlaying && "opacity-100 pointer-events-auto",
                )}
                autoplay
              />
            </Suspense>
          )}
          {!!props.film.eventInfo?.trailer && !isPlaying && (
            <motion.div {...anim()} transition={{ delay: 0.3 }} className="absolute bottom-5 right-4">
              <Button
                onClick={() => {
                  setCanPlayTrailer(true)
                  setAutoplayTrailer(true)
                  trackFilmInterest(props.film.uri!, "trailer")
                }}
                shape="branded"
                variant="primary"
                aria-label="Play Trailer"
                className="z-[1] relative"
                loading={canPlayTrailer && !isPlaying}
              >
                Play Trailer
              </Button>
            </motion.div>
          )}
        </>
      </motion.div>
    </motion.div>
  )
}

const createStyles = tv({
  base: "theme-dark bg-bg text-fg flex w-full p-[8px] rounded-[20px]",
  slots: {
    info: "flex flex-col gap-4 flex-1 p-4 justify-between w-1/2",
    media: "aspect-video relative flex-none w-1/2 rounded-[13px] overflow-hidden",
    title: "type-title-l text-balance max-w-[90%]",
    description: "type-body-m max-w-[35em] text-pretty",
    meta: "flex gap-2",
    sessions: "flex flex-col",
  },
})

function anim(): MotionProps {
  return {
    variants: {
      hide: { opacity: 0 },
      show: { opacity: 1 },
    },
  }
}
